import { QueryConstraint } from '@firebase/firestore';
import { expose } from 'comlink';
import { collection, DocumentSnapshot, getDocs, getFirestore, limit, orderBy, query, Query, startAfter, where } from 'firebase/firestore';
import { Asset } from '../../asset/asset.dto';
import { OPERATION } from '../../des.dto';
import { Warehouse } from '../../warehouse/warehouse.dto';
import { WarehouseSyncStepTaskContext } from '../executors';
import type { ProgressCallback } from '../executors';
import { AbstractWarehouseDataSyncStepWorker } from './abstract-warehouse-worker.utils';
import { ASSETS_STORE, dtoConverter, HttpUtils, updateBatch, WAREHOUSES_STORE, WW_ASSETS_COLLECTION, logger } from '@damofire/data-commons';

const DOWNLOAD_LIMIT = 1000;

export class WarehouseAssetsStepsWorker extends AbstractWarehouseDataSyncStepWorker<Asset> {
  constructor() {
    super(ASSETS_STORE, 'ASSETS');
  }

  protected async clean(ctx: WarehouseSyncStepTaskContext, onProgress?: (progress: number) => void): Promise<void> {
    logger.debug('WarehouseAssetsStepsWorker::clean', { division: ctx.division, category: 'sync' });
    return this.deleteAllDataObsoleteFromStore<Asset>(ASSETS_STORE, ctx, (dto) => dto.division._id, undefined, onProgress);
  }

  protected async push(
    ctx: WarehouseSyncStepTaskContext,
    onProgress?: (progress: number) => void,
    itemsProcessedCallback?: (change: { data: Asset; operation: OPERATION }, buffer: ArrayBuffer) => void,
  ): Promise<void> {
    logger.debug('WarehouseAssetsStepsWorker::push', { division: ctx.division, category: 'sync' });
    return super.push(ctx, onProgress, itemsProcessedCallback);
  }

  protected async resolveRemoteData(ctx: WarehouseSyncStepTaskContext, lastSnapshot: DocumentSnapshot, allFromIndex: Asset[]): Promise<[DocumentSnapshot, number]> {
    const queryConstraints: QueryConstraint[] = [limit(DOWNLOAD_LIMIT)];

    if (lastSnapshot) {
      queryConstraints.splice(0, 0, startAfter(lastSnapshot));
    }

    try {
      const q: Query = query(
        collection(getFirestore(), WW_ASSETS_COLLECTION),
        where('division._id', '==', ctx.division),
        orderBy('audit.lastModifiedDate'),
        ...queryConstraints,
      ).withConverter(dtoConverter({ syncId: ctx.syncId }));
      const queryResult = await getDocs(q);

      if (queryResult.docs.length && !this.shouldStopTask) {
        const assets = queryResult.docs.map((d) => d.data());

        logger.info('Downloaded assets from Firestore', { count: assets.length, category: 'sync' });
        await this.idbService.updateAll(this.store, assets).catch(console.error);
        return [queryResult.docs.length === DOWNLOAD_LIMIT ? queryResult.docs[queryResult.docs.length - 1] : null, queryResult.docs.length];
      }
    } catch (err) {
      logger.error('Error while downloading assets from Firestore', { error: err, category: 'sync' });
      throw err;
    }

    return [null, 0];
  }

  protected async updateTaskProgress(warehouse: string, downloaded: number, onProgress: ProgressCallback): Promise<void> {
    const warehouseData: Warehouse = await this.idbService.getByKey(WAREHOUSES_STORE, warehouse);
    if (warehouseData?.statistics?.assets) {
      const newProgress = Math.ceil((downloaded / warehouseData.statistics?.assets) * 100);
      logger.debug('Total items downloaded', { progress: newProgress, category: 'sync' });
      onProgress(newProgress, { processed: downloaded, expected: warehouseData.statistics.assets });
    }
  }

  protected async addRemoteData(data: Asset[]): Promise<void> {
    let assetsDetails = data;
    if (!Array.isArray(assetsDetails)) {
      assetsDetails = [assetsDetails];
    }

    await HttpUtils.post(this.environment.appConfig.fns, 'ww-assetsCreateBatch', {
      data: { assetsDetails },
    });
  }

  protected async deleteRemoteData(data: Asset[]): Promise<void> {
    const assetIds = data.map((a) => a._id);
    await HttpUtils.post(this.environment.appConfig.fns, 'ww-assetsDeleteAll', { data: { assetIds } });
  }

  protected async updateRemoteData(data: Asset[]): Promise<void> {
    data = data.map((asset) => {
      const { statistics, riskLevel, ...assetNoStats } = asset;
      return assetNoStats;
    });
    await updateBatch(WW_ASSETS_COLLECTION, data);
  }
}

expose(WarehouseAssetsStepsWorker);
